import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Page, Row, Column, Spinner, Loader, toast } from '@oliasoft-open-source/react-ui-library';
import {
  operationsDeselected,
  addInitialOperation,
  showImportOperationModalUpdated,
  showCreateActivityModelModalUpdated,
  clearCreateModelFlags,
  createActivityModel,
  getOperations,
  operationsCleared,
  getSections,
  templateUpdated,
  addSection,
  sectionsDeselected,
  getTimeFromOffsetWells,
  operationsResetImportExcelSuccess,
} from 'store/entities/activity-model/activity-model';
import { getWellDetails } from 'store/entities/well-details/well-details';
import {
  selectGroupedOperationListWithDefaults,
  selectImportExcelSuccess,
} from 'store/entities/activity-model/selectors';
import { CreateModelErrorsModal } from 'views/create-model/create-model-errors.modal';
import { ImportOperationModal } from 'views/activity-model/operations/import-operation.modal';
import { CreateModelModal } from 'views/create-model/create-model.modal';
import { NoItems } from 'views/no-items/no-items';
import { SectionsList } from 'views/activity-model/sections/sections.list';
import translations from 'src/internationalisation/translation-map.json';
import { useTranslation } from 'react-i18next';
import { NO_TEMPLATE } from 'src/enums/settings';
import { SectionDeleteModal } from 'views/activity-model/sections/section-delete.modal';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { DEFAULT_SECTION_INDEX } from 'src/enums/sections';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { ActivityModelDrawer } from './drawer/activity-model.drawer';
import { OperationForm } from './operations/operation.form';
import { OperationsList } from './operations/operations.list';
import { TasksList } from './tasks/tasks.list';
import { TaskForm } from './tasks/task.form';
import { OperationDeleteModal } from './operations/operation-delete.modal';
import { TaskDeleteModal } from './tasks/task-delete.modal';
import { SectionForm } from './sections/section.form';
import { TaskOffsetModal } from './tasks/task-offset.modal';
import { ImportFromTemplateModal } from './tasks/import-from-template-modal';

const SHOW_METRICS_STORAGE_KEY = 'activityModel.showMetrics';

const ActivityModel = ({
  operations,
  tasks,
  activeOperation,
  activeTask,
  isFetching,
  isAdding,
  operationsDeselected,
  addInitialOperation,
  isSchematicEmpty,
  isTemplatesEmpty,
  showImportOperationModal,
  showImportOperationModalUpdated,
  showCreateActivityModelModal,
  showCreateActivityModelModalUpdated,
  isPageDisabled,
  clearCreateModelFlags,
  createActivityModel,
  isGenerateFromSchematic,
  groupedOperationList,
  activeSection,
  getWellDetails,
  operationsCleared,
  importExcelSuccess,
  getOperations,
  getSections,
  sectionItems,
  template,
  templateUpdated,
  addSection,
  sections,
  creationErrors,
  showOffsetPreviewModal,
  getTimeFromOffsetWells,
  depthUnit,
  durationUnit,
  operationsResetImportExcelSuccess,
  isWellDetailsFetching,
}) => {
  const applyOffset = (projectId, taskWithFlag, operation) => {
    getTimeFromOffsetWells(projectId, taskWithFlag, operation);
  };
  const { t } = useTranslation();
  const { project, company } = useParams();
  const [activeForm, setActiveForm] = useState(null);
  const [operationModalVisible, setOperationModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedList, setExpandedList] = useState(groupedOperationList.map((_, idx) => idx));
  const [sectionsDeleteModalVisible, setSectionsDeleteModalVisible] = useState(false);

  const [isImportFromSection, setIsImportFromSection] = useState(false);
  const [showImportFromTemplateModal, setShowImportFromTemplateModal] = useState(false);
  const [isLinkedTaskImport, setIsLinkedTaskImport] = useState(false);

  const treeRef = useRef(null);
  const expandAllItems = () => treeRef.current.openAll();
  const collapseAllItems = () => treeRef.current.closeAll();
  const openNode = (nodeId) => treeRef.current.open(nodeId);

  const [showMetrics, setShowMetrics] = useState(() => {
    try {
      const stored = localStorage.getItem(SHOW_METRICS_STORAGE_KEY);
      return stored === null ? true : JSON.parse(stored);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_METRICS_STORAGE_KEY, JSON.stringify(showMetrics));
    } catch (error) {
      console.warn('Failed to persist activity model showMetrics preference:', error);
    }
  }, [showMetrics]);

  useEffect(() => {
    if (project) {
      getOperations(project);
      getWellDetails(project);
      getSections(project);
    }
    return () => operationsCleared();
  }, [project]);

  useEffect(() => {
    if (importExcelSuccess) {
      getOperations(project);
      getSections(project);
      operationsResetImportExcelSuccess();
    }
  }, [importExcelSuccess]);

  useEffect(() => {
    if (template !== NO_TEMPLATE) {
      toast({
        message: {
          type: template ? 'Success' : 'Error',
          content: template
            ? `${t(translations.activityModel_generatedFrom)} ${template} `
            : t(translations.activityModel_noTemplateFound),
        },
      });
      if (creationErrors.length !== 0) {
        toast({
          message: {
            type: 'Warning',
            content: (
              <>
                {creationErrors.map((error) => (
                  <>
                    <p>
                      <b>
                        Warning occurred when generating {error.type} {error.name}
                      </b>
                      <br />
                      Location: {error.section}
                      <br />
                      Warning:
                      <ul>
                        {error.messages.map((message, i) => (
                          <li key={i}>{message}</li>
                        ))}
                      </ul>
                      The {error.type} has been removed from the estimate
                    </p>
                  </>
                ))}
              </>
            ),
          },
        });
        templateUpdated(NO_TEMPLATE);
      }
    }

    return () => templateUpdated(NO_TEMPLATE);
  }, [template]);

  useEffect(() => {
    getWellDetails(project);
  }, [isGenerateFromSchematic]);

  useEffect(() => {
    if (activeTask) setActiveForm('task');
    else if (activeOperation) setActiveForm('operation');
    else if (activeSection) setActiveForm('section');
  }, [activeSection, activeOperation, activeTask]);

  const onClickAddOperation = (sectionId = null, operationHeaderPosition = null, name = 'New Operation') => {
    operationsDeselected();
    addInitialOperation(project, sectionId, operationHeaderPosition, name);
    setActiveForm('operation');
  };

  const onClickAddSection = () => {
    const section = {
      sectionId: sections[DEFAULT_SECTION_INDEX]?.sectionId,
      position: sectionItems.length,
      sectionTypeId: null,
      wellEventId: null,
      name: t(translations.activityModel_newSection),
    };
    sectionsDeselected();
    addSection(project, section);
    setActiveForm('section');
  };

  const onClickDeleteTask = (task) => {
    setSelectedId(task);
    setTaskModalVisible(true);
  };

  const handleOperationsImportModal = (isOpen) => {
    showImportOperationModalUpdated(isOpen);
  };

  const handleTaskImportModal = (isOpen) => {
    setShowImportFromTemplateModal(isOpen);
  };

  const forms = {
    section: (
      <SectionForm
        isPageDisabled={isPageDisabled}
        setActiveForm={setActiveForm}
        setSectionsDeleteModalVisible={setSectionsDeleteModalVisible}
        setSelectedId={setSelectedId}
      />
    ),
    operation: (
      <OperationForm
        operation={activeOperation}
        projectId={project}
        isPageDisabled={isPageDisabled}
        setOperationModalVisible={setOperationModalVisible}
        setSelectedId={setSelectedId}
      />
    ),
    task: (
      <TaskForm
        task={activeTask}
        operation={activeOperation}
        isPageDisabled={isPageDisabled}
        projectId={project}
        onClickDeleteTask={onClickDeleteTask}
        handleOnClickGetTimeFromOffsetWells={applyOffset}
      />
    ),
  };
  let form = null;
  if (sectionItems.length === 0 && !isFetching) {
    form = (
      <NoItems
        onClickAdd={onClickAddSection}
        showCreateModelModalUpdated={showCreateActivityModelModalUpdated}
        showImportModelModalUpdated={showImportOperationModalUpdated}
        isPageDisabled={isPageDisabled}
        type="activityModel"
        sections={sections}
      />
    );
  }
  if (activeForm) {
    form = forms[activeForm];
  }

  return (
    <>
      <Page padding={false}>
        {(isFetching || isWellDetailsFetching) && (
          <Loader text="Fetching..." theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <ActivityModelDrawer />
          <Column scroll width="320px" borderRight testId="activity-model-left-bar">
            <SectionsList
              isAdding={isAdding}
              activeSection={activeSection}
              groupedOperationList={groupedOperationList}
              setSelectedId={setSelectedId}
              isPageDisabled={isPageDisabled}
              setSectionsDeleteModalVisible={setSectionsDeleteModalVisible}
              setActiveForm={setActiveForm}
              sectionItems={sectionItems}
              onClickAddSection={onClickAddSection}
              projectId={project}
              setShowMetrics={setShowMetrics}
              showMetrics={showMetrics}
            />
          </Column>
          {activeSection && (
            <Column scroll width="320px" borderRight>
              <OperationsList
                operations={operations}
                activeSection={activeSection}
                setActiveForm={setActiveForm}
                isFetching={isFetching}
                setOperationModalVisible={setOperationModalVisible}
                setSelectedId={setSelectedId}
                isAdding={isAdding}
                projectId={project}
                expandAllItems={expandAllItems}
                onClickAddOperation={onClickAddOperation}
                handleOperationsImportModal={handleOperationsImportModal}
                showCreateActivityModelModalUpdated={showCreateActivityModelModalUpdated}
                isPageDisabled={isPageDisabled}
                isGenerateFromSchematic={isGenerateFromSchematic}
                expandedList={expandedList}
                setExpandedList={setExpandedList}
                groupedOperationList={groupedOperationList}
                setIsImportFromSection={setIsImportFromSection}
                showMetrics={showMetrics}
              />
            </Column>
          )}
          {(activeOperation || activeForm === 'operation') && (
            <Column scroll borderRight width="320px">
              <TasksList
                tasks={tasks}
                activeOperation={activeOperation}
                setActiveForm={setActiveForm}
                onClickDeleteTask={onClickDeleteTask}
                isAdding={isAdding}
                treeRef={treeRef}
                expandAllItems={expandAllItems}
                collapseAllItems={collapseAllItems}
                openNode={openNode}
                isPageDisabled={isPageDisabled}
                projectId={project}
                handleTaskImportModal={handleTaskImportModal}
                setIsLinkedTaskImport={setIsLinkedTaskImport}
                setActivityParentId={setSelectedId}
              />
            </Column>
          )}
          <Column scroll padding>
            {form}
          </Column>
        </Row>
      </Page>
      {operationModalVisible && (
        <OperationDeleteModal
          setModalVisible={setOperationModalVisible}
          operationId={selectedId}
          setActiveForm={setActiveForm}
          projectId={project}
        />
      )}
      {taskModalVisible && (
        <TaskDeleteModal
          setModalVisible={setTaskModalVisible}
          task={selectedId}
          setActiveForm={setActiveForm}
          projectId={project}
        />
      )}
      {(isSchematicEmpty || isTemplatesEmpty) && (
        <CreateModelErrorsModal
          isSchematicEmpty={isSchematicEmpty}
          isTemplatesEmpty={isTemplatesEmpty}
          company={company}
          project={project}
          clearCreateModelFlags={clearCreateModelFlags}
        />
      )}
      {showImportOperationModal && (
        <ImportOperationModal
          projectId={project}
          showImportOperationModalUpdated={showImportOperationModalUpdated}
          isImportFromSection={isImportFromSection}
          setIsImportFromSection={setIsImportFromSection}
          activeSection={activeSection}
        />
      )}
      {showCreateActivityModelModal && (
        <CreateModelModal
          projectId={project}
          showCreateModelModalUpdated={showCreateActivityModelModalUpdated}
          isFetching={isFetching}
          createModel={createActivityModel}
          isActivityModel
        />
      )}
      {sectionsDeleteModalVisible && (
        <SectionDeleteModal
          setModalVisible={setSectionsDeleteModalVisible}
          projectSectionId={selectedId}
          setActiveForm={setActiveForm}
          projectId={project}
        />
      )}
      {showOffsetPreviewModal && (
        <TaskOffsetModal
          task={activeTask}
          operation={activeOperation}
          projectId={project}
          section={activeSection}
          onApply={applyOffset}
          depthUnit={depthUnit}
          durationUnit={durationUnit}
        />
      )}
      {showImportFromTemplateModal && (
        <ImportFromTemplateModal
          handleTaskImportModal={handleTaskImportModal}
          projectId={project}
          isLinked={isLinkedTaskImport}
          activeOperation={activeOperation}
          parentId={selectedId}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    activityModel: {
      operations,
      isFetching,
      isAdding,
      isSchematicEmpty,
      isTemplatesEmpty,
      showImportOperationModal,
      showCreateActivityModelModal,
      isGenerateFromSchematic,
      activeSection,
      sectionItems,
      template,
      sections,
      creationErrors,
      showOffsetPreviewModal,
    },
  } = entities;
  const activeOperation = operations.find((item) => item.active);
  const activeTask = activeOperation?.tasks ? activeOperation.tasks.find((item) => item.active) : null;

  const isWellDetailsFetching = entities.wellDetails.isFetching;

  return {
    operations,
    isFetching,
    isAdding,
    activeOperation,
    activeTask,
    tasks: activeOperation?.tasks,
    isSchematicEmpty,
    isTemplatesEmpty,
    showImportOperationModal,
    showCreateActivityModelModal,
    isGenerateFromSchematic,
    groupedOperationList: selectGroupedOperationListWithDefaults({ entities }),
    activeSection,
    importExcelSuccess: selectImportExcelSuccess({ entities }),
    sectionItems,
    template,
    sections,
    creationErrors,
    showOffsetPreviewModal,
    depthUnit: selectUnitByQuantity(entities, QuantitiesTypes.Depth),
    durationUnit: selectUnitByQuantity(entities, QuantitiesTypes.Duration),
    isWellDetailsFetching,
  };
};

const mapDispatchToProps = {
  operationsDeselected,
  addInitialOperation,
  showImportOperationModalUpdated,
  showCreateActivityModelModalUpdated,
  clearCreateModelFlags,
  createActivityModel,
  getOperations,
  getWellDetails,
  operationsCleared,
  getSections,
  templateUpdated,
  addSection,
  getTimeFromOffsetWells,
  operationsResetImportExcelSuccess,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(ActivityModel), { isPage: true });

export { Container as ActivityModel };
